<script setup lang="ts">
import { ArrowPathIcon, CalculatorIcon, ChartBarIcon, PresentationChartLineIcon } from '@heroicons/vue/24/outline'
import { MARKETING_ROUTES, MARKETING_SCREENSHOTS } from '~~/shared/config/marketing'

definePageMeta({ layout: 'marketing' })

useMarketingSeo({
  title: 'Analyse fondamentale et valorisation d’actions | AppTrident',
  description: 'Analysez jusqu’à 20 ans de fondamentaux, comparez le cours aux bénéfices et testez des scénarios de valorisation transparents avec AppTrident.',
  path: MARKETING_ROUTES.analyze,
})

const analysisSteps = [
  { icon: ChartBarIcon, number: '01', title: 'Mesurez la qualité financière', description: 'Étudiez la croissance, les marges, la rentabilité, la dette, la dilution et la conversion des bénéfices en trésorerie.' },
  { icon: PresentationChartLineIcon, number: '02', title: 'Reliez le cours au business', description: 'Comparez l’évolution du prix aux bénéfices, au free cash flow, au cash flow opérationnel ou au chiffre d’affaires par action.' },
  { icon: CalculatorIcon, number: '03', title: 'Construisez un scénario', description: 'Choisissez un indicateur, une croissance attendue et un multiple futur pour obtenir un prix et un rendement annualisé estimés.' },
]

const comparisonQuestions = [
  'Le cours a-t-il progressé plus vite que les bénéfices ?',
  'La performance vient-elle de la croissance, de la valorisation ou des dividendes ?',
  'L’action a-t-elle réellement fait mieux que le S&P 500 ou le MSCI World ?',
  'Le scénario reste-t-il intéressant avec des hypothèses plus prudentes ?',
]
</script>

<template>
  <div>
    <MarketingFeatureHero
      eyebrow="Étape 2 · Analyser"
      title="Comprenez la qualité d’une entreprise et le prix que vous payez."
      description="Retrouvez l’historique financier, le cours de Bourse et les hypothèses de valorisation dans une même analyse pour distinguer une belle entreprise d’un achat réellement intéressant."
      :bullets="['Jusqu’à 20 ans de données et de cours.', 'Des scénarios Trident, analystes et historiques.', 'Des hypothèses toujours visibles et modifiables.']"
      page="analyser"
      preview-type="analyze"
      :preview-src="MARKETING_SCREENSHOTS.company"
      preview-alt="Page d’analyse fondamentale AppTrident présentant l’historique financier d’une entreprise"
      preview-caption="Suivez le cours, les fondamentaux et les estimations sans changer d’outil ni perdre le contexte."
    />
    <MarketingTrustStrip page="analyser" />

    <section class="bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Une analyse en trois temps" title="Les chiffres deviennent utiles lorsqu’ils répondent à une question" description="L'AppTrident organise l’analyse dans l’ordre naturel d’une décision : qualité passée, cohérence entre le cours et le business, puis valorisation future." />
        <div class="mt-12 grid divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 md:grid-cols-3 md:divide-x md:divide-y-0">
          <article v-for="step in analysisSteps" :key="step.number" class="relative p-7">
            <span class="absolute right-5 top-4 text-5xl font-black text-slate-100">{{ step.number }}</span>
            <component :is="step.icon" class="relative h-7 w-7 text-sky-blue-700" aria-hidden="true" />
            <h2 class="relative mt-7 text-lg font-bold text-slate-950">{{ step.title }}</h2>
            <p class="relative mt-3 text-sm leading-6 text-slate-600">{{ step.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <MarketingSectionHeading align="left" eyebrow="T-Graph" title="Testez ce que le prix actuel implique pour les prochaines années" description="Le T-Graph projette un indicateur fondamental, applique un multiple de valorisation futur et ajoute les dividendes attendus lorsque cela est pertinent." />
          <div class="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <p class="text-sm font-bold text-amber-950">Une estimation, jamais une promesse</p>
            <p class="mt-2 text-sm leading-6 text-amber-900/80">Chaque résultat dépend d’hypothèses sur la croissance et la valorisation. AppTrident les rend visibles pour que vous puissiez les questionner, les ajuster et tester un scénario plus prudent.</p>
          </div>
          <div class="mt-7 flex items-center gap-3 text-sm font-semibold text-slate-700"><ArrowPathIcon class="h-5 w-5 text-sky-blue-700" /> Le rendement estimé est recalculé avec le prix de marché.</div>
        </div>
        <MarketingProductPreview type="analyze" :src="MARKETING_SCREENSHOTS.valuation" alt="T-Graph AppTrident présentant un scénario de croissance, un multiple futur et un rendement estimé" caption="Les paramètres du scénario restent visibles et peuvent être adaptés en mode Avancé." />
      </div>
    </section>

    <section class="bg-slate-950 py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <MarketingProductPreview type="analyze" :src="MARKETING_SCREENSHOTS.comparison" alt="Comparaison AppTrident entre la performance d’une action, un indice et ses fondamentaux" caption="Comparez le rendement total et annualisé sur plusieurs périodes, dividendes et devises compris." />
        <div>
          <MarketingSectionHeading align="left" inverse eyebrow="Comparaisons interactives" title="Comprenez d’où vient réellement la performance" description="Comparez une entreprise avec un indice, une autre action ou l’évolution de ses propres fondamentaux sur la période qui vous intéresse." />
          <ul class="mt-8 space-y-4">
            <li v-for="question in comparisonQuestions" :key="question" class="flex gap-3 text-sm leading-6 text-slate-300"><span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-green-cucumber-400" />{{ question }}</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="bg-white py-16">
      <div class="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-4 text-center sm:px-6 md:flex-row md:text-left">
        <div><p class="text-sm font-semibold text-sky-blue-700">Étape suivante</p><h2 class="mt-2 text-2xl font-bold text-slate-950">L’analyse est faite. Quelle position renforcer et pour quel montant ?</h2></div>
        <MarketingCta :href="MARKETING_ROUTES.decide" label="Passer à la décision" page="analyser" section="next-step" destination="feature" variant="secondary" />
      </div>
    </section>

    <MarketingFinalCta page="analyser" title="Analysez une entreprise sans perdre le lien entre ses chiffres et son prix." description="Accédez aux historiques, aux T-Graphs, aux comparaisons interactives et à tout le parcours AppTrident." />
  </div>
</template>
